import {api} from "../../../../api/API";
import {sleep, useAsync2, useAsyncAction} from "nate-react-api-helpers";
import {useEffect, useRef} from "react";
import {SnackCancel, useSnackAction} from "../../shopdrawing/hardware/SnackAction";
import {retryTransient} from "../../../../misc/RetryTransient";

export function useBackgroundTask<T>(input: {
    callback: (input: T) => Promise<{ task: string }>,
    input: T,
    deps: any[],
    onDone: (result: any) => void,
}) {

    const callbackRef = useRef(input.callback);
    callbackRef.current = input.callback;

    const onDoneRef = useRef(input.onDone);
    onDoneRef.current = input.onDone;

    const mountedRef = useRef(true);
    useEffect(() => {
        return () => {
            mountedRef.current = false
        }
    }, [])

    const loader = useAsync2(async (input: T) => {
        const info = await callbackRef.current(input);
        const done = await pollBackgroundTask(info.task, mountedRef);
        if(!done) return;

        if(done.result.error) throw new Error(done.result.error);
        onDoneRef.current(done.result);
        return done.result;
    }, input.input, input.deps);

    return loader;
}

// polls the task until it's done. Returns null if the component unmounts first.
async function pollBackgroundTask(task: string, mountedRef: {current: boolean}): Promise<{ result: any } | null> {
    await sleep(500);

    while(true) {
        const status = await retryTransient(() => api.util.getBackgroundTaskStatus({task: task, withResult: true}));
        if(status.done) {
            return {result: status.result};
        }

        await sleep(1000);

        if(!mountedRef.current) {
            return null;
        }
    }
}

export type MyRefObject<T> = {
    current: T | undefined;
}

export function useBackgroundAction<T, U>(input: {
    callback: (input: T, sharedRef: MyRefObject<U>) => Promise<{ task: string }>,
    onError?: (error: any, details?: Record<string, any>) => void,
    onDone?: (result: any, sharedRef: MyRefObject<U>) => void,
}) {

    const callbackRef = useRef(input.callback);
    callbackRef.current = input.callback;

    const onDoneRef = useRef(input.onDone);
    onDoneRef.current = input.onDone;

    const onErrorRef = useRef(input.onError);
    onErrorRef.current = input.onError;

    const mountedRef = useRef(true);
    useEffect(() => {
        return () => {
            mountedRef.current = false
        }
    }, [])

    const loader = useAsyncAction(async (input: T) => {
        const ref = {current: undefined};
        const info = await callbackRef.current(input, ref);
        const done = await pollBackgroundTask(info.task, mountedRef);
        if(!done) return null;

        if(done.result.error) {
            if(onErrorRef.current) onErrorRef.current(done.result.error, done.result.details);
            throw new Error(done.result.error);
        }

        if(onDoneRef.current) onDoneRef.current(done.result, ref);
        return done.result;
    }, []);

    return loader;
}

export function useBackgroundActionSnack<T, U>(input: {
    callback: (input: T, sharedRef: MyRefObject<U>) => Promise<{ task: string }>,
    // return true when the error is shown elsewhere, to hide the snackbar error
    onError?: (error: any, details?: Record<string, any>) => boolean | void,
    onDone?: (result: any, sharedRef: MyRefObject<U>) => void,
    // returns the input to run again, or undefined to fail with the original error
    retryOnError?: (error: string, input: T) => Promise<T | undefined>,
    successMessage?: string
}) {

    const callbackRef = useRef(input.callback);
    callbackRef.current = input.callback;

    const onDoneRef = useRef(input.onDone);
    onDoneRef.current = input.onDone;

    const onErrorRef = useRef(input.onError);
    onErrorRef.current = input.onError;

    const retryOnErrorRef = useRef(input.retryOnError);
    retryOnErrorRef.current = input.retryOnError;

    const mountedRef = useRef(true);
    useEffect(() => {
        return () => {
            mountedRef.current = false
        }
    }, [])

    const loader = useSnackAction(async (input: T) => {
        const ref = {current: undefined};
        let current = input;

        while(true) {
            const info = await callbackRef.current(current, ref);
            const done = await pollBackgroundTask(info.task, mountedRef);
            if(!done) return null;

            if(done.result.error) {
                const retryInput = retryOnErrorRef.current ? await retryOnErrorRef.current(done.result.error, current) : undefined;
                if(retryInput !== undefined) {
                    current = retryInput;
                    continue;
                }

                if(onErrorRef.current?.(done.result.error, done.result.details)) return SnackCancel;
                throw new Error(done.result.error);
            }

            if(onDoneRef.current) onDoneRef.current(done.result, ref);
            return done.result;
        }
    }, {
        success: input.successMessage || "Success!",
    });

    return loader;
}